import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cn } from '@/lib/utils';
import {
  Search,
  Circle,
  ArrowRight,
  Zap,
  Box,
  FileText
} from 'lucide-react';

/**
 * Global Search - Search across machines, states, transitions, and effects
 * 
 * Opens with Ctrl+F or Cmd+F
 * Searches:
 * - Machine names
 * - State names
 * - Transition events
 * - Effect names
 */

const GlobalSearch = ({ 
  open, 
  onOpenChange, 
  machines = [], 
  onSelectMachine,
  onSelectState 
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);

  // Build searchable index from machines
  const buildSearchResults = () => {
    if (!search.trim()) return [];
    
    const query = search.toLowerCase();
    const results = [];

    machines.forEach(machine => {
      // Search machine name
      if (machine.name?.toLowerCase().includes(query)) {
        results.push({
          type: 'machine',
          machineId: machine.machine_id,
          machineName: machine.name,
          label: machine.name,
          description: `Machine with ${Object.keys(machine.states || {}).length} states`,
          icon: FileText
        });
      }

      // Search states
      Object.entries(machine.states || {}).forEach(([stateId, state]) => {
        if (stateId.toLowerCase().includes(query)) {
          results.push({
            type: 'state',
            machineId: machine.machine_id,
            machineName: machine.name,
            stateId: stateId,
            label: stateId,
            description: `State in ${machine.name}`,
            stateType: state.type || 'atomic',
            icon: Circle
          });
        }

        // Search transitions
        (state.transitions || []).forEach(transition => {
          if (transition.event?.toLowerCase().includes(query)) {
            results.push({
              type: 'transition',
              machineId: machine.machine_id,
              machineName: machine.name,
              stateId: stateId,
              event: transition.event,
              target: transition.target,
              label: transition.event,
              description: `${stateId} → ${transition.target} in ${machine.name}`,
              icon: ArrowRight
            });
          }
        });
      });

      // Search effects
      Object.keys(machine.effects || {}).forEach(effectName => {
        if (effectName.toLowerCase().includes(query)) {
          results.push({
            type: 'effect',
            machineId: machine.machine_id,
            machineName: machine.name,
            effectName: effectName,
            label: effectName,
            description: `Effect in ${machine.name}`,
            icon: Zap
          });
        }
      });
    });

    return results.slice(0, 20); // Limit to 20 results
  };

  const results = buildSearchResults();

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (results[selected]) {
          handleSelect(results[selected]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, selected, results]);

  const handleSelect = (result) => {
    onSelectMachine?.(result.machineId);
    if (result.stateId) {
      onSelectState?.(result.stateId);
    }
    onOpenChange(false);
  };

  const typeColors = {
    machine: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    state: 'bg-green-500/20 text-green-400 border-green-500/30',
    transition: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    effect: 'bg-amber-500/20 text-amber-400 border-amber-500/30'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[600px] p-0 gap-0 bg-[#0a0e27] border-white/10"
        data-testid="global-search"
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <DialogTitle>Search Machines</DialogTitle>
        </VisuallyHidden>
        
        {/* Search Input */}
        <div className="flex items-center border-b border-white/10 px-4 py-3">
          <Search className="w-5 h-5 text-gray-500 mr-3" />
          <Input
            ref={inputRef}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelected(0);
            }}
            placeholder="Search machines, states, transitions, effects..."
            className="border-0 bg-transparent focus-visible:ring-0 text-base p-0 h-auto"
            data-testid="global-search-input"
          />
          <kbd className="ml-2 px-2 py-1 text-xs bg-white/10 rounded text-gray-500">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <ScrollArea className="max-h-[400px]">
          {search.trim() && results.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Box className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No results found for "{search}"</p>
              <p className="text-xs mt-1">Try searching for machine names, states, or events</p>
            </div>
          ) : (
            <div className="p-2">
              {results.map((result, index) => {
                const Icon = result.icon;
                return (
                  <button
                    key={`${result.type}-${result.label}-${index}`}
                    onClick={() => handleSelect(result)}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors text-left",
                      selected === index
                        ? "bg-blue-500/20 border border-blue-500/30"
                        : "hover:bg-white/5 border border-transparent"
                    )}
                    data-testid={`search-result-${index}`}
                  >
                    <div className={cn(
                      "w-8 h-8 rounded flex items-center justify-center border",
                      typeColors[result.type]
                    )}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-sm truncate">{result.label}</span>
                        <Badge variant="outline" className="text-[10px] h-4 px-1.5 capitalize">
                          {result.type}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{result.description}</p>
                    </div>
                    {selected === index && (
                      <kbd className="px-2 py-0.5 text-[10px] bg-white/10 rounded text-gray-400">
                        Enter
                      </kbd>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {search.trim() && results.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-white/10 text-xs text-gray-500">
            <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
            <div className="flex items-center space-x-3">
              <span><kbd className="px-1 bg-white/10 rounded">↑↓</kbd> Navigate</span>
              <span><kbd className="px-1 bg-white/10 rounded">Enter</kbd> Select</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
